// nono-consent.js: the consent layer (CONTRACT §6, FRAMEWORK §12, Ferdig innhold 00 §08).
// Source of truth: our nono_consent cookie (Shopify's API may never upgrade it). Default off; nothing optional runs
// before an explicit choice. Wires c-consent-banner, keeps html[data-consent*] current and announces
// every choice as document event `nono:consent` {analytics, marketing}. No dependencies.

const html = document.documentElement;
const root = document.querySelector('[data-consent-root]');
const dialog = root && root.querySelector('[data-consent-dialog]');
const banner = root && root.querySelector('[data-consent-banner]');
const live = root && root.querySelector('[data-consent-live]');
// The announcement is the theme setting «Lagret»-title (group «Personvern (samtykke)»): no copy in this file.
const savedText = () => (live ? (live.dataset.savedText || '').trim() : '');
const API_TIMEOUT_MS = 6000;

// ---- anonymous tallies (POST /api/consent-choice, owner 21.10.2026) ------------------------------
// Counts only: banner variant, where the choice was made, what was chosen, phone or desktop. No id, no
// cookie, nothing stored on the device beyond one sessionStorage flag, so it needs no consent itself.
// text/plain via sendBeacon: no CORS preflight, survives the page changing right after a choice.
const tally = (choice, surface, extra = {}) => {
  const endpoint = String((window.NONO && window.NONO.endpoint) || '').replace(/\/+$/, '');
  if (!/^https:\/\//.test(endpoint) || typeof navigator.sendBeacon !== 'function') return;
  const body = JSON.stringify({
    variant: (banner && banner.dataset.consentVariant) || 'v1',
    surface,
    choice,
    device: matchMedia('(max-width: 37.5em)').matches ? 'mobile' : 'desktop',
    ...extra,
  });
  try {
    navigator.sendBeacon(`${endpoint}/api/consent-choice`, new Blob([body], { type: 'text/plain' }));
  } catch {
    /* counting never affects the page */
  }
};

let opener = null;

/** Current choice from the html attributes, or null while no choice is stored. */
const current = () =>
  html.dataset.consent === 'set'
    ? { analytics: html.dataset.consentAnalytics === '1', marketing: html.dataset.consentMarketing === '1' }
    : null;

function setAttributes({ analytics, marketing }) {
  html.dataset.consentAnalytics = analytics ? '1' : '0';
  html.dataset.consentMarketing = marketing ? '1' : '0';
  html.dataset.consent = 'set';
}

function announce(choice) {
  document.dispatchEvent(
    new CustomEvent('nono:consent', { detail: { analytics: choice.analytics, marketing: choice.marketing } }),
  );
}

// ---- Shopify Customer Privacy API --------------------------------------------------------------
// Resolves to window.Shopify.customerPrivacy, or null if it cannot be loaded (fail closed: the
// banner still works for this page view, but nothing is stored and nothing optional is assumed).
const privacyApi = new Promise((resolve) => {
  const done = (api) => resolve(api && typeof api.setTrackingConsent === 'function' ? api : null);
  const shopify = window.Shopify;
  if (shopify && shopify.customerPrivacy) return done(shopify.customerPrivacy);
  if (!shopify || typeof shopify.loadFeatures !== 'function') return done(null);
  const timer = setTimeout(() => done(null), API_TIMEOUT_MS);
  try {
    shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
      clearTimeout(timer);
      done(error ? null : window.Shopify.customerPrivacy);
    });
  } catch {
    clearTimeout(timer);
    done(null);
  }
});

// Our own record of the choice (strictly necessary: it only stores the consent decision).
// Shopify keeps its copy in the HttpOnly _shopify_essential cookie, which page scripts cannot read,
// so x-consent-state reads this one. Format: nono_consent=v1.a<0|1>.m<0|1>, 12 months.
function rememberChoice(choice) {
  document.cookie = `nono_consent=v1.a${choice.analytics ? 1 : 0}.m${choice.marketing ? 1 : 0}; Max-Age=31536000; path=/; SameSite=Lax${location.protocol === 'https:' ? '; Secure' : ''}`;
}

function store(choice) {
  rememberChoice(choice);
  return privacyApi.then(
    (api) =>
      new Promise((resolve) => {
        if (!api) return resolve(false);
        try {
          api.setTrackingConsent(
            { analytics: choice.analytics, marketing: choice.marketing, preferences: false, sale_of_data: false },
            (result) => resolve(!(result && result.error)),
          );
        } catch {
          resolve(false);
        }
      }),
  );
}

// ---- storage clean-up on withdrawal ------------------------------------------------------------

function cookieDomains() {
  const parts = location.hostname.split('.');
  const domains = [''];
  for (let i = 0; i < parts.length - 1; i += 1) domains.push(parts.slice(i).join('.'));
  return domains;
}

function deleteCookie(name) {
  for (const domain of cookieDomains()) {
    for (const prefix of ['', '.']) {
      if (!domain && prefix) continue;
      document.cookie = `${name}=; Max-Age=0; path=/${domain ? `; domain=${prefix}${domain}` : ''}; SameSite=Lax`;
    }
  }
}

function clearMarketingStorage() {
  ['_fbc', '_fbp', 'nono_attr'].forEach(deleteCookie);
  try {
    sessionStorage.removeItem('nono_booking_fbclid');
  } catch {
    /* storage blocked: nothing stored either */
  }
}

// ---- choices -----------------------------------------------------------------------------------

async function choose(choice, source, kind) {
  const wasUnknown = html.dataset.consent !== 'set';
  const picked = kind === 'accept' ? 'all' : kind === 'save' ? 'custom' : 'none';
  tally(picked, source === 'banner' ? 'banner' : wasUnknown ? 'dialog' : 'footer', picked === 'custom' ? { analytics: !!choice.analytics, marketing: !!choice.marketing } : {});
  setAttributes(choice);
  if (!choice.marketing) clearMarketingStorage();
  announce(choice);

  // One click decides (owner 19.10.2026): the banner closes at once and nothing else asks to be closed.
  // Screen readers hear «Valgene dine er lagret» from the status region; focus goes to the content.
  const fromDialog = source === 'dialog' && dialog && dialog.open;
  if (fromDialog) closeDialog(wasUnknown ? null : opener);
  else {
    const main = document.getElementById('innhold');
    if (main) main.focus({ preventScroll: true });
  }
  if (live) {
    live.textContent = '';
    requestAnimationFrame(() => {
      live.textContent = savedText();
    });
  }

  const ok = await store(choice);
  if (!ok) console.warn('[nono-consent] Customer Privacy API unavailable; choice applies to this page only.');
}

function readSwitches() {
  const pick = (purpose) => {
    const el = dialog.querySelector(`[data-consent-switch="${purpose}"]`);
    return !!el && el.getAttribute('aria-checked') === 'true';
  };
  return { analytics: pick('analytics'), marketing: pick('marketing') };
}

function syncSwitches() {
  const state = current() || { analytics: false, marketing: false };
  dialog.querySelectorAll('[data-consent-switch]').forEach((el) => {
    el.setAttribute('aria-checked', String(!!state[el.dataset.consentSwitch]));
  });
}

// ---- dialog ------------------------------------------------------------------------------------

function openDialog(trigger) {
  if (!dialog || dialog.open) return;
  opener = trigger || document.activeElement;
  syncSwitches();
  dialog.showModal();
}

function closeDialog(focusTarget) {
  if (!dialog || !dialog.open) return;
  dialog._nonoFocus = focusTarget || opener;
  dialog.close();
}

function onDialogClose() {
  const target = dialog._nonoFocus || opener;
  dialog._nonoFocus = null;
  opener = null;
  if (target && target.isConnected && target.getClientRects().length) target.focus();
  else {
    const main = document.getElementById('innhold');
    if (main) main.focus();
  }
}

// ---- wiring ------------------------------------------------------------------------------------

function onClick(event) {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;

  const openTrigger = target.closest('[data-consent-open]');
  if (openTrigger) {
    event.preventDefault();
    openDialog(openTrigger);
    return;
  }
  if (!root || !root.contains(target)) return;

  const action = target.closest('[data-consent-action]');
  if (action) {
    const inDialog = !!dialog && dialog.contains(action);
    const kind = action.dataset.consentAction;
    const choice =
      kind === 'accept'
        ? { analytics: true, marketing: true }
        : kind === 'save' && dialog
          ? readSwitches()
          : { analytics: false, marketing: false };
    choose(choice, inDialog ? 'dialog' : 'banner', kind);
    return;
  }

  const toggle = target.closest('[data-consent-switch]');
  if (toggle) {
    toggle.setAttribute('aria-checked', String(toggle.getAttribute('aria-checked') !== 'true'));
    return;
  }

  if (target.closest('[data-consent-close]')) {
    closeDialog(opener);
    return;
  }

  // Click on the backdrop (the <dialog> element itself, outside its box) closes it.
  if (target === dialog) {
    const r = dialog.getBoundingClientRect();
    const inside =
      event.clientX >= r.left && event.clientX <= r.right && event.clientY >= r.top && event.clientY <= r.bottom;
    if (!inside) closeDialog(opener);
  }
}

// Keeps scroll-padding-bottom equal to the banner's height so focused content is never hidden under it.
function trackBannerHeight() {
  if (!banner || typeof ResizeObserver !== 'function') return;
  new ResizeObserver(() => {
    const h = banner.getBoundingClientRect().height;
    html.style.setProperty('--consent-block', `${Math.ceil(h)}px`);
  }).observe(banner);
}

// The visitor's own choice (nono_consent, read by x-consent-state) is the only truth. Shopify's
// API must never upgrade it: outside the EEA it reports implicit "yes" values. If Shopify's copy
// differs from the stored choice, push the stored choice to Shopify so its own pixels follow it.
async function syncShopify() {
  // No choice yet: hold Shopify's own pixels at "no" too (it may hold an implicit or old "yes").
  // nono_consent is not written, so the banner stays up until the visitor chooses.
  const now = current() || { analytics: false, marketing: false, pending: true };
  const api = await privacyApi;
  if (!api || typeof api.currentVisitorConsent !== 'function') return;
  let visitor;
  try {
    visitor = api.currentVisitorConsent() || {};
  } catch {
    return;
  }
  const same = (visitor.analytics === 'yes') === now.analytics && (visitor.marketing === 'yes') === now.marketing;
  if (same) return;
  if (now.pending) {
    try {
      api.setTrackingConsent({ analytics: false, marketing: false, preferences: false, sale_of_data: false }, () => {});
    } catch {
      /* fail closed: our own trackers stay off regardless */
    }
  } else store(now);
}

if (root) {
  // One «shown» per tab session for a visitor without a stored choice (the banner is visible by CSS).
  if (html.dataset.consent !== 'set' && banner) {
    let first = true;
    try {
      first = !sessionStorage.getItem('nono_cs_shown');
      sessionStorage.setItem('nono_cs_shown', '1');
    } catch {
      /* storage blocked: count this page view */
    }
    if (first) tally('shown', 'banner');
  }
  document.addEventListener('click', onClick);
  if (dialog) dialog.addEventListener('close', onDialogClose);
  trackBannerHeight();
  // Clean up leftovers from before a refusal (e.g. the previous theme set _fbc before any choice).
  const state = current();
  if (state && !state.marketing) clearMarketingStorage();
  syncShopify();
}
